import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';

import {
  type ChoroplethColorStops,
  type DepartmentFeatureProperties,
  getChoroplethFillColor,
} from '@map';

import type {
  GpSurfacesMapDepartment,
  GpSurfacesMapMapDepartment,
} from '../types/gp-surfaces-map';

type DepartmentFeatureCollection = FeatureCollection<Polygon | MultiPolygon>;
export type MergedDepartmentsGeoJson = FeatureCollection<
  Polygon | MultiPolygon,
  DepartmentFeatureProperties
>;

export async function loadFranceDepartmentsGeoJson(): Promise<DepartmentFeatureCollection> {
  const response = await fetch('/geo/fr-departments.geojson');
  if (!response.ok) {
    throw new Error('Failed to load France departments GeoJSON');
  }
  return response.json();
}

export function mergeDepartmentsWithGeoJson(
  geojson: DepartmentFeatureCollection,
  departments: GpSurfacesMapMapDepartment[] | GpSurfacesMapDepartment[],
): MergedDepartmentsGeoJson {
  const byCode = new Map(
    departments.map((department) => [department.code, department]),
  );

  return {
    type: 'FeatureCollection',
    features: geojson.features.map((feature) => {
      const baseProperties = feature.properties ?? {};
      const code = String(baseProperties.code ?? '');
      const department = byCode.get(code);
      const mapDepartment = department as GpSurfacesMapMapDepartment | undefined;
      return {
        ...feature,
        properties: {
          ...baseProperties,
          code,
          name: department?.name ?? baseProperties.nom ?? code,
          total_area: department?.total_area ?? 0,
          actual_area: mapDepartment?.actual_area ?? department?.total_area ?? 0,
          map_value: mapDepartment?.map_value ?? department?.total_area ?? 0,
          projected_area: mapDepartment?.projected_area,
          projected_volume_t: mapDepartment?.projected_volume_t,
          combined_area: mapDepartment?.combined_area,
          parcelle_count: department?.parcelle_count ?? 0,
          ilot_count: department?.ilot_count ?? 0,
          carbon_points: department?.carbon_points ?? 0,
          analysis_count: department?.analysis_count ?? 0,
          granulo_points: department?.granulo_points ?? 0,
          granulo_analysis_count: department?.granulo_analysis_count ?? 0,
          total_sampling_points: department?.total_sampling_points ?? 0,
          total_analysis_count: department?.total_analysis_count ?? 0,
        },
      };
    }),
  };
}

export function enrichGeoJsonWithChoroplethColors(
  geojson: MergedDepartmentsGeoJson,
  colorStops: ChoroplethColorStops,
): MergedDepartmentsGeoJson {
  return {
    ...geojson,
    features: geojson.features.map((feature) => {
      const properties = feature.properties;
      const mapValue = properties?.map_value ?? properties?.total_area ?? 0;
      return {
        ...feature,
        properties: {
          ...properties,
          fill_color: getChoroplethFillColor(mapValue, colorStops),
        },
      };
    }),
  };
}
